import type { FC, ReactNode } from 'react'

import { ArrowDown, Plus } from 'lucide-react'
import { formatPrice } from '@/lib/format'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import PageIntro from '@/components/generals/page-intro'
import DeckleImage from '@/components/generals/deckle-image'
import Reveal from '@/components/generals/reveal'
import { primaryButton } from '@/components/generals/button-styles'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import BookingForm from '@/components/workshops/booking-form'
import {
    WORKSHOP,
    WORKSHOP_AGENDA,
    WORKSHOP_FAQ,
    WORKSHOP_INCLUDES
} from '@/constants/content'

const FACTS: Array<{ label: string; value: string }> = [
    { label: 'Duration', value: WORKSHOP.duration },
    { label: 'Starts', value: WORKSHOP.start },
    { label: 'Price', value: `${formatPrice(WORKSHOP.pricePerPerson)} per person` },
    { label: 'Group', value: `Up to ${WORKSHOP.maxGuests} guests` },
    { label: 'Open', value: WORKSHOP.days }
]

const Workshops: FC = (): ReactNode => {
    const { scrollTo } = useSmoothScroll()

    return (
        <Layout>
            <Seo
                title='Papermaking workshops'
                description='Spend four hours at a traditional water mill near Samarkand: strip mulberry bark, form your own sheets of Samarkand silk paper and polish them with an agate stone. $45 per person.'
            />

            <PageIntro
                id='workshops-intro'
                eyebrow='Half-day workshops near Samarkand'
                title='A morning at the mill'
                intro='Four unhurried hours in our courtyard, from mulberry branch to a sheet you formed yourself. No experience needed, only a willingness to get your sleeves wet.'
                aside={(
                    <button type='button' onClick={() => scrollTo('#book')} className={`${primaryButton} mt-7`}>
                        Choose a date
                        <ArrowDown className='size-4' strokeWidth={1.75} />
                    </button>
                )}
            />

            <section id='workshop-facts' aria-label='Workshop at a glance' className='container-page'>
                <Reveal>
                    <DeckleImage
                        src='/images/qogoz/atelier-courtyard.jpg'
                        alt='The atelier beside a stream with a wooden water wheel, mulberry trees and paper drying on a sunlit wall'
                        sizes='(min-width: 1360px) 1264px, 94vw'
                        shape='landscape'
                        priority
                        frameClassName='aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]'
                    />
                </Reveal>

                <dl className='mt-12 grid grid-cols-2 gap-x-6 gap-y-8 border-b border-bark/12 pb-12 sm:grid-cols-3 lg:grid-cols-5'>
                    {FACTS.map((fact, index) => (
                        <Reveal key={fact.label} delay={index * 80}>
                            <dt className='text-sm text-bark-soft'>{fact.label}</dt>
                            <dd className='mt-2 font-heading text-[1.35rem] leading-snug text-bark'>{fact.value}</dd>
                        </Reveal>
                    ))}
                </dl>
            </section>

            <section id='what-you-do' aria-labelledby='what-you-do-title' className='container-page py-20 md:py-28'>
                <div className='grid gap-14 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-4'>
                        <div className='lg:sticky lg:top-28'>
                            <Reveal>
                                <h2 id='what-you-do-title' className='font-heading text-[clamp(2.2rem,4.2vw,3.5rem)] font-light leading-[1.04] tracking-[-0.02em] text-bark'>
                                    What you do on the day
                                </h2>
                                <p className='mt-5 text-[1.02rem] leading-relaxed text-bark-soft'>
                                    Every workshop follows the paper, not the clock, but this is roughly how the morning goes.
                                </p>
                            </Reveal>

                            <Reveal delay={150} className='mt-10 rounded-[3px] bg-paper-deep/60 p-6'>
                                <h3 className='text-sm font-medium text-bark-soft'>Included</h3>
                                <ul className='mt-4 space-y-3'>
                                    {WORKSHOP_INCLUDES.map(item => (
                                        <li key={item} className='flex gap-3 text-[0.98rem] leading-snug text-bark'>
                                            <span aria-hidden='true' className='mt-2 size-1.5 shrink-0 rounded-full bg-pomegranate' />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </Reveal>
                        </div>
                    </div>

                    <ol className='relative lg:col-span-7 lg:col-start-6'>
                        <span aria-hidden='true' className='absolute bottom-6 left-[0.3rem] top-3 w-px bg-bark/15 sm:left-[5.6rem]' />
                        {WORKSHOP_AGENDA.map((item, index) => (
                            <Reveal as='li' key={item.time} delay={index * 60} className='relative grid gap-2 pb-12 pl-8 last:pb-0 sm:grid-cols-[5rem_1fr] sm:gap-10 sm:pl-0'>
                                <span aria-hidden='true' className='absolute left-0 top-2.5 size-2.5 rounded-full border border-pomegranate bg-paper sm:left-[5.3rem]' />
                                <p className='text-sm font-semibold tabular-nums text-pomegranate sm:pt-1.5 sm:text-right'>{item.time}</p>
                                <div className='sm:pl-6'>
                                    <h3 className='font-heading text-[1.6rem] font-light leading-tight text-bark'>{item.title}</h3>
                                    <p className='mt-2 max-w-xl text-[1.02rem] leading-relaxed text-bark-soft'>{item.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </ol>
                </div>
            </section>

            <section id='book' aria-labelledby='book-title' className='relative scroll-mt-16 py-4'>
                <div className='torn-edges bg-paper-deep'>
                    <div className='container-page grid gap-14 py-20 md:py-28 lg:grid-cols-12 lg:gap-8'>
                        <div className='lg:col-span-5'>
                            <Reveal>
                                <p className='flex items-center gap-4 text-sm font-medium text-bark-soft'>
                                    <span aria-hidden='true' className='h-px w-10 bg-pomegranate' />
                                    Book a workshop
                                </p>
                                <h2 id='book-title' className='mt-6 font-heading text-[clamp(2.2rem,4.2vw,3.5rem)] font-light leading-[1.04] tracking-[-0.02em] text-bark'>
                                    Pick a morning, we will put the kettle on.
                                </h2>
                                <p className='mt-5 max-w-md text-[1.02rem] leading-relaxed text-bark-soft'>
                                    Groups are kept to {WORKSHOP.maxGuests} so everyone gets time at the vat. Nothing is paid now: we confirm by email within a day, and you settle on the morning itself.
                                </p>
                            </Reveal>
                            <Reveal delay={150} className='mt-10 hidden lg:block'>
                                <DeckleImage
                                    src='/images/qogoz/step-forming.jpg'
                                    alt='Hands lifting a reed mould with a fresh sheet of paper from the vat'
                                    sizes='34vw'
                                    className='w-[78%]'
                                />
                            </Reveal>
                        </div>

                        <Reveal delay={100} className='lg:col-span-6 lg:col-start-7'>
                            <div className='rounded-[3px] border border-bark/12 bg-paper p-6 shadow-[0_24px_50px_-36px_rgba(58,42,31,0.5)] sm:p-9'>
                                <BookingForm />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section id='faq' aria-labelledby='faq-title' className='container-page py-20 md:py-28'>
                <div className='grid gap-10 lg:grid-cols-12 lg:gap-8'>
                    <Reveal className='lg:col-span-4'>
                        <h2 id='faq-title' className='font-heading text-[clamp(2rem,3.6vw,3rem)] font-light leading-tight text-bark'>
                            Good to know
                        </h2>
                    </Reveal>
                    <div className='divide-y divide-bark/12 border-y border-bark/12 lg:col-span-7 lg:col-start-6'>
                        {WORKSHOP_FAQ.map(item => (
                            <details key={item.question} className='group py-2'>
                                <summary className='flex cursor-pointer list-none items-center justify-between gap-6 rounded-sm py-4 text-left focus-visible:outline-2 focus-visible:outline-bark [&::-webkit-details-marker]:hidden'>
                                    <span className='font-heading text-[1.35rem] text-bark'>{item.question}</span>
                                    <Plus aria-hidden='true' className='size-5 shrink-0 text-pomegranate transition-transform duration-500 group-open:rotate-45' strokeWidth={1.5} />
                                </summary>
                                <p className='max-w-2xl pb-5 text-[1.02rem] leading-relaxed text-bark-soft'>{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default Workshops
